import React from "react";
import { Navigation } from "../components/Navigation";
import { COPY } from "../constants/copy";

interface LetterFromHeartProps {
  onNext: () => void;
  onPrevious: () => void;
}

export const LetterFromHeart: React.FC<LetterFromHeartProps> = ({
  onNext,
  onPrevious,
}) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400 py-20 relative overflow-hidden">
      {/* Animated background decoration */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Soft gradient orbs */}
        <div className="absolute -top-32 left-1/4 w-80 h-80 bg-gradient-to-br from-blushPink to-softRose opacity-5 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-32 right-1/4 w-80 h-80 bg-gradient-to-tr from-heartRed to-blushPink opacity-5 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "1.5s" }}
        ></div>

        {/* Floating decorative elements */}
        <div
          className="absolute top-32 right-10 text-4xl opacity-10 animate-float"
          style={{ animationDuration: "7s" }}
        >
          ✦
        </div>
        <div
          className="absolute bottom-40 left-8 text-5xl opacity-10 animate-float"
          style={{ animationDuration: "8s", animationDelay: "1s" }}
        >
          ✦
        </div>
      </div>

      <div className="max-w-2xl w-full relative z-10">
        {/* Header */}
        <div
          className="text-center mb-12 animate-fade-in"
          style={{ animation: "fadeIn 1s ease-out" }}
        >
          <div className="mb-4">
            <span className="text-4xl">❤️</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-light text-deepCharcoal dark:text-darkText mb-3">
            {COPY.letter.title}
          </h2>
          <div className="h-px w-12 bg-gradient-to-r from-transparent via-heartRed to-transparent dark:via-softRose mx-auto"></div>
        </div>

        {/* Letter content */}
        <div className="space-y-5 md:space-y-6 mb-12">
          {COPY.letter.paragraphs.map((paragraph, idx) => (
            <div
              key={idx}
              style={{
                animation: `fadeInUp 1.2s ease-out forwards`,
                animationDelay: `${idx * 0.4}s`,
              }}
            >
              {/* Paragraph content */}
              <p className="text-lg md:text-xl text-deepCharcoal dark:text-darkText font-light leading-relaxed text-center">
                {paragraph}
              </p>
            </div>
          ))}

          {/* Final state message */}
          <div
            style={{
              animation: `fadeInUp 1.2s ease-out forwards`,
              animationDelay: `${COPY.letter.paragraphs.length * 0.4}s`,
            }}
            className="text-center pt-6"
          >
            <div className="inline-block">
              <p className="text-sm text-mutedGray dark:text-gray-400 font-light italic tracking-widest uppercase mb-3">
                With all my heart
              </p>
              <div className="h-px w-20 bg-gradient-to-r from-transparent via-heartRed to-transparent dark:via-softRose mx-auto"></div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center">
          <Navigation
            onNext={onNext}
            onPrevious={onPrevious}
            nextLabel={COPY.letter.button}
            showPrevious={true}
          />
        </div>

        {/* Bottom decoration */}
        <div className="text-center mt-12">
          <div className="text-2xl text-softRose dark:text-blushPink opacity-20">
            ✦ ✦ ✦
          </div>
        </div>
      </div>

      {/* Custom animations with easing */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
